/**
 * signingSetup — pre-commit /signing/start orchestration (S1.2, 2026-06-11).
 *
 * CartCommitService step 4 (DisclosurePrefill) hard-requires a SigningSession
 * row, and step 5 (SignNowDispatch) requires cart.SigningGroupId — both are
 * created server-side by POST /signing/start (SignNowFunction, atomic with
 * rollback; sandbox carts get a synthetic group + session). Nothing in the
 * dispatcher-era RAO flow called it any more, so every commit died at
 * DISCLOSURE_PREFILL_NO_SIGNING_SESSION.
 *
 * This helper is the single shared implementation used by:
 *   - PreSubmitConfirmModal (Round-3-locked Screen 27 ceremony — primary path)
 *   - CommitFlow.submitCommit (re-confirm / retry path)
 *
 * It is idempotent: when cart.signingGroupId is already bound it returns
 * immediately. Throws Error with PLAIN-LANGUAGE messages — callers surface
 * them directly to the FSR.
 */
import type {
  CartDto,
  DisclosureTemplate,
  EntityRule,
  EntityRuleRequiredDoc,
  Product,
  StartSigningRequest,
} from '@retail-ops/shared-types';
import type { ApiClient } from '@retail-ops/api-client';
import { parsePeople, parseProducts, parseEntityType } from '../utils/cartStateParse';
import { resolveDisclosures, buildSigners } from '../disclosure';

/** Slice of ConfigContext the disclosure engine needs. */
export interface SigningSetupConfig {
  disclosures: ReadonlyArray<DisclosureTemplate>;
  entityRules: ReadonlyArray<EntityRule>;
  entityRequiredDocs?: ReadonlyArray<EntityRuleRequiredDoc>;
  products: ReadonlyArray<Product>;
}

/** Live progress raised while the disclosure package builds (REQ-167 ph2). */
export interface SigningPrepareProgress {
  phase: 'preparing' | 'starting';
  currentDoc?: number;
  totalDocs?: number;
  estimatedRemainingMs?: number | null;
}

export interface EnsureSigningSessionArgs {
  cart: CartDto;
  client: ApiClient;
  config: SigningSetupConfig;
  /** FSR audit identity (BankRep — never a signer). */
  fsrEmail?: string;
  fsrName?: string;
  signal?: AbortSignal;
  /** Per-document build progress ("Preparing document N of M…"). */
  onProgress?: (p: SigningPrepareProgress) => void;
}

const PREPARE_POLL_MS = 750;
const PREPARE_TIMEOUT_MS = 180_000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * REQ-167 phase 2 (Bill 2026-07-23) — run the disclosure build as an async
 * prepare job (POST /signing/prepare-package byCart mode) and poll
 * /signing/prepare-status for live per-document progress. Returns the
 * PackageId on success, or null on ANY failure — the caller then falls back
 * to the legacy synchronous /signing/start (identical server pipeline, just
 * without progress), so submits never regress on a prepare hiccup.
 * A user abort (AbortError) propagates — that's a cancel, not a fallback.
 */
async function tryPreparePackage(
  args: EnsureSigningSessionArgs,
  templateCodes: ReadonlyArray<string>,
): Promise<string | null> {
  const { cart, client, signal, onProgress } = args;
  try {
    const prep = await client.signing.preparePackage(
      {
        CartId: cart.cartId,
        TemplateCodes: [...templateCodes],
        FileName: 'Disclosure Package',
      },
      signal ? { signal } : undefined,
    );
    if (!prep.Success || !prep.JobId) return null;
    const jobId = prep.JobId;
    const deadline = Date.now() + PREPARE_TIMEOUT_MS;
    for (;;) {
      await delay(PREPARE_POLL_MS);
      const st = await client.signing.getPrepareStatus(jobId, signal ? { signal } : undefined);
      if (st.Status === 'complete' && st.PackageId) return st.PackageId;
      if (st.Status === 'failed') return null;
      onProgress?.({
        phase: 'preparing',
        ...(typeof st.CurrentDoc === 'number' ? { currentDoc: st.CurrentDoc } : {}),
        ...(typeof st.TotalDocs === 'number' ? { totalDocs: st.TotalDocs } : {}),
        estimatedRemainingMs: typeof st.EstimatedRemainingMs === 'number' ? st.EstimatedRemainingMs : null,
      });
      if (Date.now() > deadline) return null;
    }
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') throw err;
    return null; // fall back to the legacy synchronous start
  }
}

/**
 * Ensure the cart has a SigningSession + SigningGroupId before commit.
 * No-op when cart.signingGroupId is already bound.
 */
export async function ensureSigningSessionForCart(
  args: EnsureSigningSessionArgs,
): Promise<void> {
  const { cart, client, config, fsrEmail, fsrName, signal } = args;
  if (cart.signingGroupId) return; // already bound — idempotent re-submit

  const people = parsePeople(cart);
  const products = parseProducts(cart);
  const entityType = parseEntityType(cart);

  // The SERVER decides which documents the package holds (BACKLOG-1665,
  // 2026-10-01). DisclosureRenderOrchestrator.ResolveAsync applies the
  // per-template presence conditions (beneficiaries:*, product:interest-bearing,
  // feature:checks-ordered, feature:overdraft-elected) that the client-side
  // resolver below knows nothing about. Asking for the client's superset made
  // StartSigning refuse every POD cart ("Missing document(s):
  // POD_DESIG_CONTINGENT, POD_DESIG_MINOR") because the server had rightly
  // excluded the two variants that did not apply. The local resolver remains
  // only as the fallback when the resolve call itself fails.
  let templateCodes: string[] = [];
  try {
    const server = await client.retail.disclosures.resolve(
      { cartId: cart.cartId },
      signal ? { signal } : undefined,
    );
    templateCodes = Array.from(new Set(server.resolved.map((r) => r.templateCode)));
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') throw err;
    console.warn('[signingSetup] server resolve failed — falling back to the client-side resolver', err);
  }
  if (templateCodes.length === 0) {
    const resolved = resolveDisclosures({
      ...(entityType ? { entityType } : {}),
      products,
      people,
      ...(cart.branch ? { branchCode: cart.branch } : {}),
      disclosureTemplates: config.disclosures,
      entityRules: config.entityRules,
      entityRequiredDocs: config.entityRequiredDocs,
      productCatalog: config.products,
    });
    templateCodes = Array.from(new Set(resolved.map((r) => r.templateCode)));
  }
  if (templateCodes.length === 0) {
    throw new Error(
      'We couldn’t build the disclosure package for this cart — no documents '
      + 'resolved for the selected products. Check the cart’s products and try again.',
    );
  }

  // In-branch ceremony — signers are present with the FSR, so no email/SMS
  // invite fires; the capture surfaces run on the Signing step after the
  // pipeline pauses at awaiting-signatures.
  // Products decide who signs — NOT person.role (Bill 2026-07-16: "Roles should never
  // be cart based"). Without this, anyone merely present in the cart was handed the
  // kiosk: a minor on no product e-consented and signed both signature cards and the
  // overdraft form. See feedback_roles_are_never_cart_based_always_account_party_20260716.
  const signers = buildSigners(people, 'in-person', products as unknown as ReadonlyArray<Record<string, unknown>>);
  if (signers.length === 0) {
    throw new Error(
      'No signers found on the cart. Add at least a Primary owner on the '
      + 'People step before submitting.',
    );
  }

  // REQ-167 phase 2 — async prepare with live per-document progress, then
  // start on the cached PackageId (skips the orchestrator re-render). Any
  // prepare failure returns null → legacy byCartAndTemplates start below
  // (Models/SignNowModels.cs StartSigningRequest): CartId + TemplateCodes
  // route through DisclosureRenderOrchestrator server-side. Either mode
  // persists Cart.SigningGroupId + the SigningSession row (CartId present).
  const packageId = await tryPreparePackage(args, templateCodes);

  const req: StartSigningRequest = {
    Signers: signers,
    CartId: cart.cartId,
    ...(packageId ? { PackageId: packageId } : { TemplateCodes: templateCodes }),
    ...(cart.branch ? { BranchCode: cart.branch } : {}),
    ...(fsrEmail ? { FsrEmail: fsrEmail } : {}),
    ...(fsrName ? { FsrName: fsrName } : {}),
    FileName: 'Disclosure Package',
  };

  args.onProgress?.({ phase: 'starting' });
  const res = await client.signing.start(req, signal ? { signal } : undefined);
  if (!res.success) {
    const reason = res.error ?? 'the signing service did not accept the package';
    throw new Error(`We couldn’t prepare the signing package: ${reason}`);
  }
}
